import type { HTMLAttributes, ReactNode } from 'react';

/**
 * `soft` is the hi-fi's `.card.soft`: the tinted card it uses for the one thing
 * on a screen that matters most — today's session, the coach's note on it, the
 * confirmation that the session is done.
 *
 * It is a **prop rather than a class the caller passes**, because
 * `className="bg-primary-tint"` and the base `bg-surface-raised` are two
 * utilities of equal specificity, and which of them wins is decided by the order
 * Tailwind happens to emit them in rather than by the order they are written.
 * That is not something a screen should have to know. Here there is one
 * declaration per tone and no collision to lose.
 *
 * Both grounds are in the contrast table in `src/index.css`: `ink` measures
 * 18.84:1 on the raised card and 15.26:1 on the tinted one.
 */
export type CardTone = 'raised' | 'soft';

const TONES: Record<CardTone, string> = {
  raised: 'bg-surface-raised border-border',
  soft: 'bg-primary-tint border-primary-tint',
};

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  tone?: CardTone;
  children: ReactNode;
}

export function Card({ tone = 'raised', className, ...rest }: CardProps) {
  return (
    <div
      className={['rounded-[--radius-card] border p-5 sm:p-6', TONES[tone], className ?? ''].join(
        ' ',
      )}
      {...rest}
    />
  );
}

export function CardTitle({ className, ...rest }: HTMLAttributes<HTMLHeadingElement>) {
  return <h1 className={['text-ink text-xl font-semibold', className ?? ''].join(' ')} {...rest} />;
}

export function CardBody({ className, ...rest }: HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p
      className={['text-ink-soft mt-2 text-sm leading-relaxed', className ?? ''].join(' ')}
      {...rest}
    />
  );
}
